import React, { useState, useRef, useEffect } from 'react';

import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { Skeleton } from '@material-ui/lab';
import styled from 'styled-components';
import { StyledBox } from 'styles/common';

import { generateKey } from 'constants/helper';
import { StyledIconV2 } from 'constants/icons';

import { StyledDimensionColumnCell } from '../GrowthTableHelperComps';
import { StyledTextArea } from './styledComps';

export const BULLET_POINT_UUID_PREFIX = '_bulletPoint_';

const Loader = () => (
  <StyledBox>
    <Skeleton width="100%" />
    <Skeleton width="100%" />
  </StyledBox>
);

const DataCell = ({ dataObj, contentType, handleChange }) => (
  <StyledDimensionColumnCell>
    {dataObj?.loading ? (
      <Loader />
    ) : (
      <>
        {contentType === 'PARAGRAPH' ? (
          <ParagraphDataCell paragraphText={dataObj?.paragraphText} onChange={handleChange} />
        ) : (
          <ListDataCell listPoints={dataObj?.listPoints} onChange={handleChange} />
        )}
      </>
    )}
  </StyledDimensionColumnCell>
);

export const ParagraphDataCell = ({ paragraphText, onChange }) => {
  const ref = useRef(null);
  const [localParagraphText, setLocalParagraphText] = useState(paragraphText);

  const handleBlur = () => {
    onChange({ paragraphText: localParagraphText });
    if (ref?.current) ref.current.blur();
  };

  const handleInputChange = (ev, value) => {
    setLocalParagraphText(ev.target.value);
  };

  useEffect(() => {
    setLocalParagraphText(paragraphText);
  }, [paragraphText]);

  return (
    <StyledTextArea
      ref={ref}
      onBlur={handleBlur}
      placeholder="Add Summary"
      width="100%"
      value={localParagraphText}
      onChange={handleInputChange}
      rowsMin={1}
    />
  );
};

export const ListDataCell = ({ listPoints, onChange }) => {
  const ref = useRef(null);
  const [triggerSave, setTriggerSave] = useState(false);
  const [nextFocus, setNextFocus] = useState(null);
  const [localListPoints, setLocalListPoints] = useState(listPoints);

  const handleSave = () => {
    if (ref?.current) {
      if (!ref.current.matches(':focus-within')) {
        setTriggerSave(true);
      }
    }
  };

  const onListPointEdit = (index, text, reason) => {
    const changedListPoint = localListPoints[index];
    let updatedListPoints = localListPoints;

    if (reason === 'backspace') {
      setNextFocus(index > 0 ? index - 1 : 0);
      if (text === '') {
        updatedListPoints = updatedListPoints.filter((_, ind) => ind !== index);
      }
    }
    if (reason === 'enter') {
      if (text === '') {
        setNextFocus(index);
        updatedListPoints = updatedListPoints.filter((_, ind) => ind !== index);
      } else {
        setNextFocus(index + 1);
        updatedListPoints = [
          ...updatedListPoints.slice(0, index),
          { ...changedListPoint, text },
          { uuid: `${BULLET_POINT_UUID_PREFIX}${generateKey(5)}`, text: '' },
          ...updatedListPoints.slice(index + 1),
        ];
      }
    }
    if (reason === 'blur') {
      setNextFocus(null);
      if (text === '') {
        updatedListPoints = updatedListPoints.filter((_, ind) => ind !== index);
      } else {
        updatedListPoints = [
          ...updatedListPoints.slice(0, index),
          { ...changedListPoint, text },
          ...updatedListPoints.slice(index + 1),
        ];
      }
    }

    setLocalListPoints(updatedListPoints);
  };

  const addListPoint = (index, text, reason) => {
    if (reason === 'backspace') {
      setNextFocus(index > 0 ? index - 1 : 0);
    }
    if (reason === 'enter') {
      setNextFocus(index + 1);
    }
    if (reason === 'blur') {
      setNextFocus(null);
    }

    if (text === '') return;
    setLocalListPoints([...localListPoints, { uuid: `${BULLET_POINT_UUID_PREFIX}${generateKey(5)}`, text }]);
  };

  /* whenever prop.listPoints change update local state as well. */
  useEffect(() => {
    setLocalListPoints(listPoints);
  }, [listPoints]);

  /* the save call is executed by the following effect as the state might be stale if we
    directly update on blur of container div
  */
  useEffect(() => {
    if (triggerSave) {
      onChange({ listPoints: localListPoints });
      setTriggerSave(false);
    }
  }, [triggerSave]);

  return (
    <div ref={ref} tabIndex="-1" onBlur={handleSave}>
      {localListPoints.map(({ uuid, text }, index) => (
        <EditableListItem
          key={`${uuid}+${index}`}
          text={text}
          onEdit={(text, reason = 'blur') => onListPointEdit(index, text, reason)}
          autoFocus={nextFocus === index}
        />
      ))}
      <EditableListItem
        key={generateKey(5)}
        text=""
        onEdit={(text, reason = 'blur') => addListPoint(localListPoints.length, text, reason)}
        autoFocus={nextFocus === localListPoints.length}
        isDummy
      />
    </div>
  );
};

/* onEdit expects to return a promise */
const EditableListItem = ({ text, onEdit, isDummy = false, autoFocus = false }) => {
  const ref = useRef(null);
  const [localText, setLocalText] = useState(text);

  const handleBlur = () => {
    onEdit(localText, 'blur');
  };

  const handleKeyDown = (event) => {
    if (event.key.toLowerCase() === 'enter') {
      event.preventDefault();
      event.stopPropagation();
      onEdit(localText, 'enter');
    }
    if (event.key.toLowerCase() === 'backspace') {
      console.log('pressed back space');
      if (localText === '') {
        event.preventDefault();
        event.stopPropagation();
        onEdit(localText, 'backspace');
      }
    }
  };

  const handleInput = (event) => {
    setLocalText(event.target.value);
  };

  useEffect(() => {
    if (autoFocus && ref?.current) {
      ref.current.focus();
      ref.current.setSelectionRange(localText.length, localText.length);
    }
  }, [autoFocus]);

  return (
    <Container>
      <IconContainer>
        <StyledIconV2 size="fixed10px" containerSize="fixed24px" circularOutline color="#C4C4C4">
          <StyledCircleDot />
        </StyledIconV2>
      </IconContainer>
      <StyledTextArea
        autoFocus={autoFocus}
        placeholder="Add summary point"
        width="100%"
        ref={ref}
        rowsMin={1}
        value={localText}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        style={{
          fontSize: '14px',
          lineHeight: '21px',
          paddingTop: '2px',
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const IconContainer = styled.div`
  flex: 0;
  display: flex;

  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
`;

const StyledCircleDot = styled(FiberManualRecordIcon)`
  circle {
    r: 12;
  }
`;

export default DataCell;
